import {useMemo, useState} from 'react';
import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import LFCChartsInputScaleDialog, {
  initialScaleValue,
  ScaleValue
} from './components/LFCChartsInputScale';
import {isEmpty} from 'lodash';

/**
 * 引数
 */
interface Props {
  title: string;
  source: any;
  yLeft: {
    type: string;
    name: string;
    dsColumn: any;
    markPoint?: any;
    markLine?: any;
  };
  yRight: {
    type: string;
    name: string;
    dsColumn: any;
    markPoint?: any;
    markLine?: any;
  };
  grid?: {
    top?: number | string;
    bottom?: number | string;
    left?: number | string;
    right?: number | string;
  };
  color?: string[];
  height?: string;
  exportData?: any;
  exportFields?: any;
  exportFilename?: string;
  onClick?: (prm: {data: any}) => void;
  inputScale?: boolean;
  xList: any;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
}

/**
 * LFCChartsComboYMD
 * @param props
 * @returns
 */
const LFCChartsComboYMD2 = (props: Props) => {
  const [scaleValue, setScaleValue] = useState<ScaleValue>(initialScaleValue);
  const [inputScaleOpen, setInputScaleOpen] = useState(false);
  // スケール変更
  const onApply = (scaleValue: ScaleValue) => {
    setScaleValue(scaleValue);
  };

  // 実績ワーク数合計を計算
  const totalWorkCount = useMemo(() => {
    if (!props.source || props.source.length === 0) return 0;

    // 左軸のデータカラム名（通常は'total_count'）を使用して合計を計算
    const columnName = props.yLeft.dsColumn;
    return props.source.reduce((sum, item) => {
      const value = Number(item[columnName]) || 0;
      return sum + value;
    }, 0);
  }, [props.source, props.yLeft.dsColumn]);

  // 不良ワーク数合計を計算
  const totalFailureCount = useMemo(() => {
    if (!props.source || props.source.length === 0) return 0;

    // 不良数カラム（通常は'ng'）を使用して合計を計算
    return props.source.reduce((sum, item) => {
      const value = Number(item['ng']) || 0;
      return sum + value;
    }, 0);
  }, [props.source]);

  // 正しい不良率平均値を計算（不良ワーク数合計/実績ワーク数合計*100）
  const correctAvgDefectRate = useMemo(() => {
    if (totalWorkCount === 0) return 0;
    return (totalFailureCount / totalWorkCount) * 100;
  }, [totalFailureCount, totalWorkCount]);

  return useMemo(() => {
    return (
      <div>
        <LFCBaseCharts
          option={{
            title: {
              text: props.title
            },
            grid: props.grid
              ? props.grid
              : {
                  top: '65',
                  left: '60',
                  right: '63',
                  bottom: '93'
                },
            legend: {
              type: 'scroll',
              bottom: 0,
              itemWidth: 10
            },
            tooltip: {
              trigger: 'axis'
              // formatter:  (params: any) => {
              //   return (
              //     !isEmpty(props.xList) ? params[0].name +
              //       "<br/>" +
              //       t("検査数(個)") +
              //       params[0].value.total_count +
              //       "<br/>" +
              //       t("不良率(%)") +
              //       ((params[0].value.ng / params[0].value.total_count) * 100).toFixed(1) +
              //       "<br/>"
              //       : params[0].data.lq_time +
              //       "<br/>" +
              //       t("検査数(個)") +
              //       params[0].value.total_count +
              //       "<br/>" +
              //       t("不良率(%)") +
              //       ((params[0].value.ng / params[0].value.total_count) * 100).toFixed(1) +
              //       "<br/>"
              //   );
              // },
            },
            dataZoom: [
              {
                type: 'inside'
              }
            ],
            color: props.color ? props.color : null,
            dataset: {
              source: props.source
            },
            xAxis: [
              {
                type: 'category',
                axisLabel: {
                  rotate: '45',
                  fontSize: 10
                },
                data: !isEmpty(props.xList) ? props.xList : props.source.map((a: any) => a.id)
              }
            ],
            yAxis: [
              {
                type: 'value',
                name: props.yLeft.name,
                nameTextStyle: {
                  color: props.color ? props.color[0] : null
                },
                axisLabel: {
                  color: props.color ? props.color[0] : null
                },
                axisLine: {
                  lineStyle: {
                    color: props.color ? props.color[0] : null
                  }
                },
                axisTick: {
                  lineStyle: {
                    color: props.color ? props.color[0] : null
                  }
                },
                min: scaleValue.y1AxisMin,
                max: scaleValue.y1AxisMax,
                interval: scaleValue.y1AxisInterval
              },
              {
                type: 'value',
                name: props.yRight.name,
                nameTextStyle: {
                  color: props.color ? props.color[1] : null
                },
                axisLabel: {
                  color: props.color ? props.color[1] : null
                },
                axisLine: {
                  lineStyle: {
                    color: props.color ? props.color[1] : null
                  }
                },
                axisTick: {
                  lineStyle: {
                    color: props.color ? props.color[1] : null
                  }
                },
                min: scaleValue.y2AxisMin,
                max: scaleValue.y2AxisMax,
                interval: scaleValue.y2AxisInterval
              }
            ],
            series: [
              {
                type: props.yLeft.type,
                name: props.yLeft.name,
                xAxisIndex: 0,
                yAxisIndex: 0,
                encode: {
                  // x: props.x.dsColumn,
                  y: props.yLeft.dsColumn
                },
                markPoint: props.yLeft.markPoint,
                // markLine: props.yLeft.markLine,
                markLine: {
                  data: props.yLeft.markLine,
                  silent: false,
                  animation: true
                }
              },
              {
                type: props.yRight.type,
                name: props.yRight.name,
                xAxisIndex: 0,
                yAxisIndex: 1,
                encode: {
                  // x: props.x.dsColumn,
                  y: props.yRight.dsColumn
                },
                markPoint: props.yRight.markPoint,
                markLine: {
                  // 正しい不良率平均値を使用したカスタムmarkLine
                  data: [
                    {
                      yAxis: correctAvgDefectRate,
                      label: {
                        position: 'end',
                        formatter: `Ave. ${correctAvgDefectRate.toFixed(2)}%`,
                        color: '#FF9800'
                      },
                      lineStyle: {
                        color: '#FF9800'
                      }
                    }
                  ],
                  symbol: props.yRight.markLine ? props.yRight.markLine.symbol : 'none',
                  silent: false,
                  animation: true
                }
              }
            ],
            animation: true
          }}
          exportData={props.exportData}
          exportFields={props.exportFields}
          exportFilename={props.exportFilename}
          height={props.height}
          onEvents={{
            click: (params: any) => {
              if (props.onClick != null) {
                props.onClick(params);
              }
            }
          }}
          inputScaleStatus={props.inputScale !== undefined ? props.inputScale : false}
          inputScaleOpen={() => setInputScaleOpen(true)}
          totalWorkCount={totalWorkCount}
          zoomCharts={props.zoomCharts ? props.zoomCharts : undefined}
          zoomChartsClose={props.zoomChartsClose ? props.zoomChartsClose : undefined}
          zoomChartId={props.zoomChartId ? props.zoomChartId : undefined}
          zoomStatus={props.zoomChartId === props.zoomedChartId}
        />
        <LFCChartsInputScaleDialog
          open={inputScaleOpen}
          onClose={() => setInputScaleOpen(false)}
          onApply={onApply}
          y1AxisName={props.yLeft.name}
          y2AxisName={props.yRight.name}
          inputY1Axis={true}
          inputY2Axis={true}
        />
      </div>
    );
  }, [
    props.source,
    props.title,
    props.grid,
    props.color,
    props.xList,
    props.yLeft.name,
    props.yLeft.type,
    props.yLeft.dsColumn,
    props.yLeft.markPoint,
    props.yLeft.markLine,
    props.yRight.name,
    props.yRight.type,
    props.yRight.dsColumn,
    props.yRight.markPoint,
    props.yRight.markLine,
    props.exportData,
    props.exportFields,
    props.exportFilename,
    props.height,
    props.onClick,
    props.inputScale,
    props.zoomCharts,
    props.zoomChartsClose,
    props.zoomChartId,
    props.zoomedChartId,
    inputScaleOpen,
    correctAvgDefectRate,
    scaleValue,
    totalWorkCount
  ]);
};
export default LFCChartsComboYMD2;
